import React from 'react';
import { useNavigate } from 'react-router-dom';

const DataUsageOverview: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-canvas text-text-primary py-24 px-6 md:px-12 relative">
            {/* Close Button - Top Right */}
            <nav className="fixed top-0 left-0 right-0 z-50 pointer-events-none safe-top">
                <div className="relative pointer-events-auto flex items-center justify-end h-20 px-6 md:px-12 w-full">
                    <button
                        onClick={() => navigate('/')}
                        className="text-gray-400 hover:text-gray-300 transition-colors p-2 focus-visible:outline-2 focus-visible:outline-white focus-visible:outline-offset-4 focus:outline-none z-10"
                        aria-label="Close data usage overview"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </nav>

            <div className="max-w-4xl mx-auto">
                <h1 className="text-5xl md:text-7xl font-light tracking-[-0.02em] mb-4 text-center">
                    Data Usage Overview
                </h1>
                <p className="text-sm text-text-secondary text-center opacity-60 mb-12">
                    Last updated: December 2, 2025
                </p>

                <div className="space-y-8 text-text-primary font-light leading-relaxed">
                    <section>
                        <p className="text-lg text-text-primary">
                            This summary explains what data Nura collects and how it's used. For complete details, please see our full Privacy Policy.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
                            What We Collect
                        </h2>

                        <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
                            Account Information
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li><strong className="text-text-primary">Name:</strong> Your first name (optional)</li>
                            <li><strong className="text-text-primary">Email address:</strong> Required for account creation</li>
                            <li><strong className="text-text-primary">Authentication data:</strong> Via Sign in with Apple or email</li>
                        </ul>

                        <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
                            Meditation Data
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li><strong className="text-text-primary">Pre-session check-ins:</strong> Your selected emotion, energy level, and focus level</li>
                            <li><strong className="text-text-primary">Session metadata:</strong> Title, duration, start/end times, completion status</li>
                            <li><strong className="text-text-primary">Meditation history:</strong> Which meditations you access and complete</li>
                            <li><strong className="text-text-primary">Audio playback tracking:</strong> For progress tracking and personalization</li>
                        </ul>

                        <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
                            Journal Entries
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li>Text content you write in your meditation journal</li>
                            <li>Timestamps for each entry</li>
                        </ul>

                        <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
                            User Preferences
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li>Experience level (Beginner, Intermediate, Advanced)</li>
                            <li>Onboarding goals/intents</li>
                            <li>Notification preferences (daily reminder settings)</li>
                            <li>HealthKit integration preference (optional)</li>
                        </ul>

                        <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
                            Subscription Data (from Apple)
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li>Subscription status (active, trial, expired)</li>
                            <li>Subscription tier (monthly/annual)</li>
                            <li>Trial dates and subscription dates</li>
                            <li>Transaction IDs (for subscription management)</li>
                            <li>Subscription lifecycle events used for RevenueCat analytics</li>
                        </ul>
                        <p className="text-lg text-text-secondary mt-4">
                            <strong className="text-text-primary">Note:</strong> We do NOT collect payment information (credit cards, billing addresses). All payments are processed by Apple.
                        </p>

                        <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
                            Optional HealthKit Data
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li>Only collected if you enable Apple Health integration</li>
                            <li>Mindful minutes sessions: Start time, end time, duration</li>
                            <li>Stored locally on your device and synced to iCloud (if enabled)</li>
                        </ul>

                        <h3 className="text-xl md:text-2xl font-normal text-text-primary mb-3 mt-6">
                            Technical Data
                        </h3>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li>Device information (model, operating system)</li>
                            <li>App usage metrics (for app improvement)</li>
                            <li>Crash logs (for bug fixes)</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
                            How We Use Your Data
                        </h2>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li>To provide personalized guided meditation sessions</li>
                            <li>To track your meditation progress and history</li>
                            <li>To sync your data across devices when logged in</li>
                            <li>To manage your subscription and provide premium features</li>
                            <li>To improve the app (using aggregated, non-identifying data)</li>
                            <li>To provide customer support</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
                            Where Your Data Is Stored
                        </h2>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li><strong className="text-text-primary">Supabase:</strong> Our backend service provider stores your account data, meditation sessions, journal entries, and settings</li>
                            <li><strong className="text-text-primary">Your Device:</strong> Some data is stored locally for offline access</li>
                            <li><strong className="text-text-primary">Apple:</strong> Authentication data (Sign in with Apple), subscription data (StoreKit), and optional health data (HealthKit)</li>
                            <li><strong className="text-text-primary">RevenueCat:</strong> Subscription analytics and lifecycle event monitoring</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
                            Data Sharing
                        </h2>
                        <p className="text-lg text-text-primary mb-4">
                            We do NOT sell your data or share it with advertisers. We only share data with:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li><strong className="text-text-primary">Supabase:</strong> Our backend service provider (for data storage and sync)</li>
                            <li><strong className="text-text-primary">Apple:</strong> For authentication, subscription processing, and optional health data storage</li>
                            <li><strong className="text-text-primary">RevenueCat:</strong> For subscription analytics and lifecycle event monitoring</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
                            Your Rights
                        </h2>
                        <p className="text-lg text-text-primary mb-4">
                            You can:
                        </p>
                        <ul className="list-disc list-inside space-y-2 text-lg text-text-primary ml-4">
                            <li><strong className="text-text-primary">Access your data:</strong> View all your data in the app</li>
                            <li><strong className="text-text-primary">Export your data:</strong> Settings → Account → Export Data</li>
                            <li><strong className="text-text-primary">Delete your account:</strong> Settings → Account → Delete Account</li>
                            <li><strong className="text-text-primary">Disable HealthKit:</strong> Settings → Apple Health → Toggle off</li>
                            <li><strong className="text-text-primary">Manage notifications:</strong> Settings → Notifications</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl md:text-3xl font-normal text-text-primary mb-4">
                            Questions?
                        </h2>
                        <p className="text-lg text-text-primary">
                            For more details, see our full{' '}
                            <a href="/app-privacy" className="text-text-primary hover:opacity-60 transition-opacity underline">
                                Privacy Policy
                            </a>
                            . If you have questions, please contact us at{' '}
                            <a href="mailto:support@nurameditation.com" className="text-text-primary hover:opacity-60 transition-opacity">
                                support@nurameditation.com
                            </a>
                            .
                        </p>
                    </section>

                    <section className="pt-8 border-t border-white/10">
                        <p className="text-sm text-text-secondary opacity-60">
                            Publisher: SilentLabs LLC — 30 N Gould St Ste 52683, Sheridan.
                        </p>
                        <p className="text-sm text-text-secondary opacity-60 mt-2">
                            Last updated: December 2, 2025
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default DataUsageOverview;
